import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, ComponentType, lazy } from 'react';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import ScrollToTop from './components/ScrollToTop';
import { AdminAuthProvider } from './admin/AdminAuthContext';

// Helper to retry lazy imports on chunk load failure (happens after deployments)
function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      // Check if it's a chunk load error
      if (
        error instanceof Error &&
        (error.message.includes('Failed to fetch dynamically imported module') ||
          error.message.includes('Loading chunk') ||
          error.message.includes('Loading CSS chunk'))
      ) {
        // Reload the page to get fresh assets
        window.location.reload();
        // Return a dummy component while reloading
        return { default: (() => null) as unknown as T };
      }
      throw error;
    }
  });
}

// Lazy load pages with auto-retry on chunk failures
const Home = lazyWithRetry(() => import('./pages/Home'));
const About = lazyWithRetry(() => import('./pages/About'));
const Instructors = lazyWithRetry(() => import('./pages/Instructors'));
const InstructorProfile = lazyWithRetry(() => import('./pages/InstructorProfile'));
const Reviews = lazyWithRetry(() => import('./pages/Reviews'));
const Contact = lazyWithRetry(() => import('./pages/Contact'));
const Courses = lazyWithRetry(() => import('./pages/Courses'));
const CourseDetail = lazyWithRetry(() => import('./pages/CourseDetail'));
const Schedule = lazyWithRetry(() => import('./pages/Schedule'));
const Individual = lazyWithRetry(() => import('./pages/Individual'));
const Products = lazyWithRetry(() => import('./pages/Products'));
const Booking = lazyWithRetry(() => import('./pages/Booking'));
const BookingStatus = lazyWithRetry(() => import('./pages/BookingStatus'));
const PaymentSuccess = lazyWithRetry(() => import('./pages/PaymentSuccess'));
const MasterCourse = lazyWithRetry(() => import('./pages/MasterCourse'));
const MasterCourseApply = lazyWithRetry(() => import('./pages/MasterCourseApply'));
const MasterCourseApplySuccess = lazyWithRetry(() => import('./pages/MasterCourseApplySuccess'));
const LegalOffer = lazyWithRetry(() => import('./pages/legal/Offer'));
const LegalConsentDataProcessing = lazyWithRetry(() => import('./pages/legal/ConsentDataProcessing'));
const LegalConsentMarketing = lazyWithRetry(() => import('./pages/legal/ConsentMarketing'));
const LegalConsentDataDistribution = lazyWithRetry(() => import('./pages/legal/ConsentDataDistribution'));
const LegalPrivacyPolicy = lazyWithRetry(() => import('./pages/legal/PrivacyPolicy'));
const PublicOffer = lazyWithRetry(() => import('./pages/legal/PublicOffer'));
const PublicPrivacyPolicy = lazyWithRetry(() => import('./pages/legal/PublicPrivacyPolicy'));
const PublicConsentDataProcessing = lazyWithRetry(() => import('./pages/legal/PublicConsentDataProcessing'));
const PublicConsentMarketing = lazyWithRetry(() => import('./pages/legal/PublicConsentMarketing'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));

// Admin pages
const AdminLayout = lazyWithRetry(() => import('./admin/AdminLayout'));
const AdminLogin = lazyWithRetry(() => import('./admin/pages/Login'));
const AdminDashboard = lazyWithRetry(() => import('./admin/pages/Dashboard'));
const AdminCourses = lazyWithRetry(() => import('./admin/pages/Courses'));
const AdminCourseForm = lazyWithRetry(() => import('./admin/pages/CourseForm'));
const AdminSchedules = lazyWithRetry(() => import('./admin/pages/Schedules'));
const AdminBookings = lazyWithRetry(() => import('./admin/pages/Bookings'));
const AdminInstructors = lazyWithRetry(() => import('./admin/pages/Instructors'));
const AdminReviews = lazyWithRetry(() => import('./admin/pages/Reviews'));
const AdminInquiries = lazyWithRetry(() => import('./admin/pages/Inquiries'));
const AdminBookingDetail = lazyWithRetry(() => import('./admin/pages/BookingDetail'));
const AdminPageContent = lazyWithRetry(() => import('./admin/pages/PageContent'));
const AdminCustomers = lazyWithRetry(() => import('./admin/pages/Customers'));
const AdminSettings = lazyWithRetry(() => import('./admin/pages/Settings'));
const AdminMasterCourseApplications = lazyWithRetry(() => import('./admin/pages/MasterCourseApplications'));
const AdminMasterCourseApplicationDetail = lazyWithRetry(() => import('./admin/pages/MasterCourseApplicationDetail'));
const AdminPaymentLinks = lazyWithRetry(() => import('./admin/pages/PaymentLinks'));
const AdminPaymentLinkNew = lazyWithRetry(() => import('./admin/pages/PaymentLinkNew'));
const AdminPaymentLinkDetail = lazyWithRetry(() => import('./admin/pages/PaymentLinkDetail'));
const PayLink = lazyWithRetry(() => import('./pages/PayLink'));
const PayLinkSuccess = lazyWithRetry(() => import('./pages/PayLinkSuccess'));

function App() {
  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <ScrollToTop />
      <Routes>
        {/* Home page - full viewport, no layout */}
        <Route path="/" element={<Home />} />

        {/* Master course - editorial dark theme, no main layout */}
        <Route path="/master-course" element={<MasterCourse />} />

        {/* Personal payment link pages — dark editorial theme, no layout */}
        <Route path="/pay/:linkCode" element={<PayLink />} />
        <Route path="/pay/:linkCode/success" element={<PayLinkSuccess />} />
        <Route path="/master-course/apply" element={<MasterCourseApply />} />
        <Route path="/master-course/apply/success" element={<MasterCourseApplySuccess />} />
        <Route path="/master-course/legal/offer" element={<LegalOffer />} />
        <Route path="/master-course/legal/consent-data-processing" element={<LegalConsentDataProcessing />} />
        <Route path="/master-course/legal/consent-marketing" element={<LegalConsentMarketing />} />
        <Route path="/master-course/legal/consent-data-distribution" element={<LegalConsentDataDistribution />} />
        <Route path="/master-course/legal/privacy-policy" element={<LegalPrivacyPolicy />} />

        {/* Pages with standard layout */}
        <Route element={<Layout />}>
          <Route path="/about" element={<About />} />
          <Route path="/instructors" element={<Instructors />} />
          <Route path="/instructors/:id" element={<InstructorProfile />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:slug" element={<CourseDetail />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/individual" element={<Individual />} />
          <Route path="/products" element={<Products />} />
          <Route path="/booking" element={<Navigate to="/courses" replace />} />
          <Route path="/booking/:courseId" element={<Booking />} />
          <Route path="/booking/status/:bookingNumber" element={<BookingStatus />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
        </Route>

        {/* Public legal pages — light theme, no main layout */}
        <Route path="/legal/offer" element={<PublicOffer />} />
        <Route path="/legal/privacy-policy" element={<PublicPrivacyPolicy />} />
        <Route path="/legal/consent-data-processing" element={<PublicConsentDataProcessing />} />
        <Route path="/legal/consent-marketing" element={<PublicConsentMarketing />} />

        {/* Admin routes */}
        <Route path="/admin/*" element={
          <AdminAuthProvider>
            <Routes>
              <Route path="/login" element={<AdminLogin />} />
              <Route element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="courses" element={<AdminCourses />} />
                <Route path="courses/:id" element={<AdminCourseForm />} />
                <Route path="schedules" element={<AdminSchedules />} />
                <Route path="bookings" element={<AdminBookings />} />
                <Route path="bookings/:id" element={<AdminBookingDetail />} />
                <Route path="instructors" element={<AdminInstructors />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="inquiries" element={<AdminInquiries />} />
                <Route path="pages" element={<AdminPageContent />} />
                <Route path="customers" element={<AdminCustomers />} />
                <Route path="master-course-applications" element={<AdminMasterCourseApplications />} />
                <Route path="master-course-applications/:id" element={<AdminMasterCourseApplicationDetail />} />
                <Route path="payment-links" element={<AdminPaymentLinks />} />
                <Route path="payment-links/new" element={<AdminPaymentLinkNew />} />
                <Route path="payment-links/:id" element={<AdminPaymentLinkDetail />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
            </Routes>
          </AdminAuthProvider>
        } />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default App;
